import {
  forwardRef, useRef, useMemo, useEffect, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import classNames from 'classnames/bind';

import { checkAndRoun } from '../../../../../../helpers/topicsHelpers';
import { UK_PRIVATE_COMPANY_INSIGHTS_PATH } from '../../../../../../data/ukPrivateCompanyInsights';
import {
  ONE_YEAR, FIVE_YEAR, ALL,
} from '../../../../../../data/directory/topic_classes/ChartFilters';
import InsiderSvc from '../../../../../../services/dbServices/InsiderSvc';
import { UK_INSIGHTS, UK_INSIGHTS_TRIAL } from '../../../../../../data/features';
import convertPermalinkToPath from '../../../../../../helpers/urlHelpers';

import Preloader from '../../../../../Preloader';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const ChartModal = forwardRef(({
  onClose,
  style,
  x,
  y,
  date,
  currentValue,
  activeFilter,
  activeType,
  natureOfBusiness,
}, ref) => {
  const cfMainWrapper = useRef(document.querySelector('.cf-main-wrapper'));
  const styles = useMemo(() => ({
    top: `${y}px`,
    left: `${x}px`,
    ...style,
  }), [x, y, style]);
  const [currentFundraising, setCurrentFundraising] = useState({
    isLoading: true,
    currentData: [],
  });
  const hasInsightsAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS)
  ));
  const hasInsightsTrialAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS_TRIAL)
  ));
  const isTypeMonth = activeFilter === ONE_YEAR || activeFilter === FIVE_YEAR
    || activeFilter === ALL;

  useEffect(() => {
    let mounted = true;

    if (!date) return;

    setCurrentFundraising({
      isLoading: true,
      currentData: [],
    });
    const callAPI = async () => {
      const params = {
        per: 4,
      };
      if (isTypeMonth) {
        params.start_date = moment(date).startOf('month').format('YYYY-MM-DD');
        params.end_date = moment(date).endOf('month').format('YYYY-MM-DD');
      } else {
        params.start_date = date;
        params.end_date = date;
      }
      const fundraisingData = await InsiderSvc.getCompanyInsiderFundraising(params);

      if (!mounted) return;
      if (fundraisingData && fundraisingData.length) {
        setCurrentFundraising({
          isLoading: false,
          currentData: fundraisingData,
        });
      } else {
        setCurrentFundraising({
          isLoading: false,
          currentData: [],
        });
      }
    };

    callAPI();

    return () => {
      mounted = false;
    };
  }, [date]);

  const mainDateFormat = isTypeMonth ? 'MMM YYYY' : 'DD MMM YYYY';
  const seeMoreDateFormat = isTypeMonth ? 'MM-YYYY' : 'DD-MM-YYYY';
  const natureURL = natureOfBusiness ? `/${natureOfBusiness}` : '';

  return (
    createPortal(
      <div
        className={cx('chart-modal', { no_access: !hasInsightsAccess && !hasInsightsTrialAccess })}
        ref={ref}
        style={styles}
      >
        <div className={Styles['chart-modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
        <div className={Styles['chart-modal-header']}>
          {moment(date).format(mainDateFormat)}
        </div>
        <div className={Styles['chart-modal-sub']}>
          {activeType === 'count' ? (
            <>
              {currentValue || 0}
            </>
          ) : (
            <>
              {checkAndRoun(currentValue, 2, true)}
            </>
          )}
        </div>
        <div className={Styles['chart-modal-lists']}>
          <div className={Styles['chart-modal-list']}>
            <div className={Styles['chart-modal-list-htitle']}>Top Fundraisers</div>
            <div className={Styles['chart-modal-list-hvalue']}>
              GBP
            </div>
          </div>
          {currentFundraising.isLoading ? (
            <div>
              <Preloader
                loading
                static
                top="unset"
              />
            </div>
          ) : (
            <>
              {!currentFundraising.currentData.length ? (
                <div className={Styles['chart-modal-no-data']}>
                  No data found
                </div>
              ) : (
                <>
                  {currentFundraising.currentData
                    .slice(0, 3)
                    .map((obj, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={i} className={Styles['chart-modal-list']}>
                        <Link to={convertPermalinkToPath(obj.companyPermalink)} className={Styles['chart-modal-list-title']}>
                          {obj.name}
                        </Link>
                        <div className={Styles['chart-modal-list-value']}>
                          {obj.raisedAmount ? checkAndRoun(obj.raisedAmount, 2, true) : '-'}
                        </div>
                      </div>
                    ))}
                </>
              )}
            </>
          )}
        </div>
        {!currentFundraising.isLoading && currentFundraising.currentData.length && (
          <div className={Styles['chart-modal-footer']}>
            <Link
              to={`${UK_PRIVATE_COMPANY_INSIGHTS_PATH}/top-equity-fundraisers/date/${moment(date).format(seeMoreDateFormat)}${natureURL}`}
            >
              See All
            </Link>
          </div>
        )}
      </div>,
      cfMainWrapper.current,
    )
  );
});

export default ChartModal;
